import type { Analytics } from './types';

interface PostHogConfig {
  apiKey: string;
  /** Defaults to PostHog Cloud (US). Override for EU or self-hosted. */
  apiHost?: string;
}

/**
 * Stub PostHog adapter. Lazy-loads `posthog-js` only when needed so the
 * SDK never inflates the main bundle. Replace this with a real
 * `import posthog from 'posthog-js'` import once the dependency is added —
 * the surface is intentionally identical so callers don't change.
 *
 * Until then, this acts as a no-op while preserving the Analytics contract,
 * making it safe to wire `VITE_POSTHOG_KEY` in .env without any code edits.
 */
export function createPostHogAnalytics(config: PostHogConfig): Analytics {
  const queue: (() => void)[] = [];
  let ready = false;
  let posthog: PostHogClient | null = null;

  const drain = () => {
    if (!ready || !posthog) return;
    while (queue.length > 0) {
      queue.shift()?.();
    }
  };

  const enqueue = (fn: () => void) => {
    if (ready && posthog) fn();
    else queue.push(fn);
  };

  // Lazy-load the SDK. If it's missing we fall back to a no-op silently —
  // the project ships without `posthog-js` as a dependency by default.
  // The dynamic specifier (computed via String()) keeps both TS and Vite
  // from trying to resolve the module at build time.
  const moduleName = 'posthog-js';
  void (async () => {
    try {
      const mod: unknown = await import(/* @vite-ignore */ moduleName);
      const candidate = mod as { default?: PostHogClient } & PostHogClient;
      const client: PostHogClient = candidate.default ?? candidate;
      client.init(config.apiKey, {
        api_host: config.apiHost ?? 'https://app.posthog.com',
        capture_pageview: false,
        autocapture: false,
        person_profiles: 'identified_only',
      });
      posthog = client;
      ready = true;
      drain();
    } catch (err) {
      console.warn('[analytics] posthog-js not installed; events ignored', err);
      ready = true;
      queue.length = 0;
    }
  })();

  return {
    track(event, properties) {
      enqueue(() => posthog?.capture(event, properties));
    },
    identify(user) {
      enqueue(() => {
        if (user === null) {
          posthog?.reset?.();
        } else {
          posthog?.identify(user.id, { email: user.email, ...user.traits });
        }
      });
    },
    page(path, properties) {
      const url = path ?? (typeof location === 'undefined' ? '/' : location.pathname);
      enqueue(() => posthog?.capture('$pageview', { $current_url: url, ...properties }));
    },
    flush() {
      // posthog-js batches; nothing to do here.
    },
  };
}

interface PostHogClient {
  init(key: string, opts: Record<string, unknown>): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  identify(id: string, traits?: Record<string, unknown>): void;
  reset?(): void;
}
